/**
 * กิลด์ (F4 or the HUD menu): the guild window, ported from the 3D client (archive/apps-client-3d/src/ui/guild.ts) into
 * the liked .ls-card frame, with a กิลด์ศัตรู tab; plus the kill banner's HUD timer and its feed (toasts).
 * The rules are in the shared World (systems/guild.ts, guildWar.ts, guildBanner.ts); this file sends commands and
 * reads `progress.guild` and `sys.guildBanner`. Decides no rule.
 */
import {
  GUILD,
  GUILD_BANNER,
  GUILD_BANNER_MARKS,
  GUILD_COLORS,
  GUILD_LEVELS,
  GUILD_RANKS,
  GUILD_REGISTRAR_REACH,
  GUILD_REGISTRAR_TITLE,
  GUILD_WAR,
  GUILD_WAR_MODES,
  guildLevel,
  guildRank,
  guildSize,
  guildWarPhase,
  rankOrder,
  SIM_GUILDS,
  type GuildRankId,
  type GuildState,
  type GuildWarMode,
  type World,
  type WorldEvent,
} from '@nine/shared';
import { lsCard, type LsCard } from './card';
import { $, fmtClock, h } from './dom';
import { registerUi, type UiContext } from './registry';

const CSS = `
#guildWin.ls-card{position:fixed;left:50%;top:50%;bottom:auto;right:auto;transform:translate(-50%,-50%);width:min(560px,calc(100vw - 20px));max-height:calc(100vh - 20px);overflow-y:auto;z-index:60;gap:8px;padding:16px 18px;font-size:14px;line-height:1.5}
#guildWin[hidden]{display:none}
#guildWin h3{margin:0;font-size:18px;display:flex;justify-content:space-between;align-items:center}
#guildWin h3 button{font-size:16px;min-width:36px;min-height:36px;cursor:pointer}
#guildWin .gd-tabs{display:flex;flex-wrap:wrap;gap:4px;margin-bottom:6px}
#guildWin button.gd-b{min-height:30px;padding:0 10px;cursor:pointer;border:1px solid rgba(244,241,234,.3);background:rgba(22,19,15,.6);color:#d4cec3;border-radius:5px;font-size:12px}
#guildWin button.gd-b.on,#guildWin button.gd-b.main{background:#a3281f;border-color:#d6412f;color:#fff}
#guildWin button.gd-b:disabled{opacity:.45;cursor:default}
#guildWin .gd-row{display:flex;align-items:center;gap:8px;padding:3px 4px;border-bottom:1px solid rgba(244,241,234,.12)}
#guildWin .gd-grow{flex:1;min-width:0}
#guildWin .gd-note{margin:2px 0;color:#d4cec3;font-size:12px}
#guildWin .gd-hot{color:#f6c46c}
#guildWin input,#guildWin select{background:rgba(12,9,8,.6);color:#f4f1ea;border:1px solid rgba(244,241,234,.3);border-radius:4px;padding:3px 6px;font:inherit;font-size:13px;min-width:0}
#guildWin input[type=text]{flex:1}
#guildWin .gd-swatch{width:22px;height:22px;border-radius:3px;border:2px solid transparent;cursor:pointer}
#guildWin .gd-swatch[aria-pressed=true]{border-color:#f6c46c}
#guildWin .gd-banner{border:1px solid #c8322b;background:rgba(120,20,16,.25);border-radius:6px;padding:6px 8px}
#gbTimer{position:fixed;top:64px;left:50%;transform:translateX(-50%);z-index:40;display:flex;gap:8px;align-items:center;padding:4px 12px;border:1px solid #c79a2b;border-radius:4px;background:rgba(18,12,10,.82);color:#f4f1ea;font-size:13px;pointer-events:none;box-shadow:0 0 0 1px rgba(0,0,0,.6)}
#gbTimer[hidden]{display:none}
#gbTimer .gb-glyph{font-size:16px;color:#e0503f;font-weight:700}
#gbTimer .gb-time{font-variant-numeric:tabular-nums;color:#f6c46c}
`;

type Tab = 'members' | 'info' | 'war' | 'enemies';
const TABS: [Tab, string][] = [
  ['members', 'สมาชิก'],
  ['info', 'ข้อมูล'],
  ['war', 'ศึกกิลด์'],
  ['enemies', 'กิลด์ศัตรู'],
];
const fmt = (n: number) => Math.floor(n).toLocaleString('th-TH');
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const localMinute = (d = new Date()): number => d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
const guildName = (w: World, id: string) => SIM_GUILDS.find((s) => s.id === id)?.name ?? [...w.players.values()].find((s) => s.progress.guild?.id === id)?.progress.guild?.name ?? id;

class GuildUi {
  private readonly card: LsCard;
  private tab: Tab = 'members';
  private key = '';
  private foundName = '';
  private foundColor: string = GUILD_COLORS[0];
  private draftNotice: string | null = null;
  private clockT = 0;
  private lastWar = '';
  private readonly timer = h('div', { id: 'gbTimer', hidden: true, role: 'status' });

  constructor(private readonly ctx: UiContext) {
    document.head.append(h('style', {}, CSS));
    this.card = lsCard({ id: 'guildWin', title: 'กิลด์', key: 'F4', menuId: 'mbGuild', refreshEvery: 1e9, draw: (b) => this.draw(b) });
    $('#hud').append(this.timer);
    addEventListener('keydown', (e) => {
      if (e.key !== 'F4' || (e.target as HTMLElement | null)?.tagName === 'INPUT') return;
      e.preventDefault();
      this.card.toggle();
      this.key = '';
    });
  }

  private get w(): World {
    return this.ctx.world;
  }

  escape(): boolean {
    if (!this.card.isOpen) return false;
    this.card.close();
    return true;
  }

  show(tab?: Tab): void {
    if (tab) this.tab = tab;
    this.key = '';
    this.card.open();
  }

  handle(ev: WorldEvent): void {
    if (ev.type === 'guild' && ev.what === 'registrar') this.show('info');
    if (ev.type === 'guildWar' && ev.what === 'end') {
      this.lastWar = `${ev.won ? 'ชนะ' : 'แพ้'} · ${ev.points.join(' : ')} · ได้ ${fmt(ev.gold)} ตำลึง`;
    }
    if (ev.type !== 'guildBanner') return;
    if (ev.what === 'raise') this.ctx.toast(`ปักป้ายสังหารกิลด์ ${ev.target} · ${Math.round(ev.left / 60)} นาที`);
    else if (ev.what === 'kill') this.ctx.toast(`${GUILD_BANNER_MARKS.hunted.glyph} ${ev.killer} สังหาร ${ev.victim} · ${ev.ours} : ${ev.theirs}`);
    else if (ev.what === 'end') this.ctx.toast(`ป้ายสังหารต่อ ${ev.target} สิ้นสุด · ผล ${ev.ours} : ${ev.theirs}`);
    this.key = '';
  }

  sync(dt: number): void {
    this.syncTimer();
    if ((this.clockT -= dt) <= 0) {
      this.clockT = 300;
      // Online, the server keeps the clock (guildWarClock is refused from players).
      if (!new URLSearchParams(location.search).has('server')) this.ctx.send({ t: 'guildWarClock', minute: localMinute() });
    }
    if (!this.card.isOpen) return;
    const g = this.w.progress.guild;
    const sec = this.tab === 'war' ? Math.floor(localMinute() * 60) : 0;
    const key = JSON.stringify([this.tab, g ?? null, this.nearRegistrar(), sec, this.w.progress.gold, this.foundColor, this.lastWar, g?.banner ? Math.ceil(g.banner.left) : Math.ceil(g?.bannerCool ?? 0)]);
    if (key === this.key) return;
    const typing = this.card.root.contains(document.activeElement) && document.activeElement?.tagName === 'INPUT';
    this.key = key;
    if (typing) return;
    this.card.redraw();
  }

  private syncTimer(): void {
    const g = this.w.progress.guild;
    const b = g?.banner;
    this.timer.hidden = !b;
    if (!b) return;
    const text = `${b.targetName}|${Math.ceil(b.left)}|${b.ours}|${b.theirs}`;
    if (this.timer.dataset.k === text) return;
    this.timer.dataset.k = text;
    this.timer.replaceChildren(
      h('span', { class: 'gb-glyph' }, GUILD_BANNER_MARKS.hunted.glyph),
      h('span', {}, `ป้ายสังหาร · ${b.targetName}`),
      h('span', { class: 'gb-time' }, fmtClock(Math.max(0, Math.ceil(b.left)))),
      h('span', {}, `${b.ours} : ${b.theirs}`),
    );
  }

  private nearRegistrar(): boolean {
    let any = false;
    const p = this.w.player;
    for (const e of this.w.entities.values()) {
      if (e.kind !== 'npc' || e.title !== GUILD_REGISTRAR_TITLE) continue;
      any = true;
      if (Math.hypot(e.x - p.x, e.y - p.y) <= GUILD_REGISTRAR_REACH) return true;
    }
    return !any;
  }

  private btn(label: string, on: () => void, main = false, disabled = false): HTMLButtonElement {
    const b = h('button', { type: 'button', class: main ? 'gd-b main' : 'gd-b' }, label);
    b.disabled = disabled;
    b.addEventListener('click', () => {
      on();
      this.key = '';
    });
    return b;
  }

  private row(...kids: (Node | string)[]): HTMLElement {
    return h('div', { class: 'gd-row' }, ...kids);
  }

  private draw(body: HTMLElement): void {
    body.replaceChildren();
    const g = this.w.progress.guild;
    const title = this.card.root.querySelector('h3')!.firstChild!;
    title.textContent = `${g ? g.name : 'กิลด์'} (F4)`;
    if (!g) return this.drawNone(body);
    const tabs = h('div', { class: 'gd-tabs', role: 'tablist' });
    for (const [id, label] of TABS) {
      const b = this.btn(label, () => {
        this.tab = id;
        this.card.redraw();
      });
      b.classList.toggle('on', id === this.tab);
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', String(id === this.tab));
      tabs.append(b);
    }
    body.append(tabs);
    if (this.tab === 'members') this.drawMembers(body, g);
    else if (this.tab === 'info') this.drawInfo(body, g);
    else if (this.tab === 'war') this.drawWar(body, g);
    else this.drawEnemies(body, g);
  }

  private drawNone(body: HTMLElement): void {
    const near = this.nearRegistrar();
    const pr = this.w.progress;
    body.append(h('p', { class: 'gd-note' }, near ? 'ท่านยังไม่มีกิลด์ ก่อตั้งเองหรือเข้าร่วมกิลด์ที่มีอยู่' : 'ไปหานายทะเบียนกิลด์เพื่อก่อตั้งหรือเข้ากิลด์'));
    const name = h('input', { type: 'text', maxLength: GUILD.nameMax, placeholder: `ชื่อกิลด์ (ไม่เกิน ${GUILD.nameMax} ตัว)`, value: this.foundName });
    name.addEventListener('input', () => (this.foundName = name.value));
    const sw = this.row();
    for (const c of GUILD_COLORS) {
      const s = h('button', { type: 'button', class: 'gd-swatch', title: c });
      s.style.background = c;
      s.setAttribute('aria-pressed', String(c === this.foundColor));
      s.addEventListener('click', () => {
        this.foundColor = c;
        this.card.redraw();
      });
      sw.append(s);
    }
    const found = this.btn(`ก่อตั้งกิลด์ (${fmt(GUILD.foundCost)} ตำลึง)`, () => this.ctx.send({ t: 'guildFound', name: name.value.trim(), color: this.foundColor }), true, !near || pr.gold < GUILD.foundCost);
    body.append(this.row(name, found), sw);
    for (const s of SIM_GUILDS) {
      const lv = GUILD_LEVELS.filter((l) => s.contribution >= l.contribution).length;
      body.append(this.row(h('span', { class: 'gd-grow' }, `${s.name} · ระดับ ${lv} · ${s.members.length + 1} คน`), this.btn('เข้าร่วม', () => this.ctx.send({ t: 'guildJoin', id: s.id }), false, !near)));
    }
  }

  private drawMembers(body: HTMLElement, g: GuildState): void {
    const mine = guildRank(g.rank);
    body.append(h('p', { class: 'gd-note' }, `สมาชิก ${guildSize(g)} / ${guildLevel(g).cap} คน`));
    const rows = [{ name: this.w.progress.name, rank: g.rank, me: true }, ...g.members.map((m) => ({ name: m.name, rank: m.rank, me: false }))];
    rows.sort((a, b) => rankOrder(a.rank) - rankOrder(b.rank));
    for (const m of rows) {
      const row = this.row(h('span', { class: 'gd-grow' }, m.me ? `${m.name} (ท่าน)` : m.name));
      if (!m.me && mine.canPromote) {
        const sel = h('select', { title: 'ตำแหน่ง' });
        for (const r of GUILD_RANKS) sel.append(h('option', { value: r.id, selected: r.id === m.rank }, r.name));
        sel.addEventListener('change', () => this.ctx.send({ t: 'guildRank', name: m.name, rank: sel.value as GuildRankId }));
        row.append(sel);
      } else row.append(h('span', {}, guildRank(m.rank).name));
      if (!m.me && mine.canKick && rankOrder(m.rank) > rankOrder(g.rank)) row.append(this.btn('เชิญออก', () => this.ctx.send({ t: 'guildKick', name: m.name })));
      body.append(row);
    }
    if (mine.canInvite) {
      const who = h('input', { type: 'text', placeholder: 'ชื่อผู้ที่จะเชิญ (เว้นว่าง: ผู้ฝึกยุทธ์อิสระ)' });
      body.append(this.row(who, this.btn('เชิญเข้ากิลด์', () => this.ctx.send({ t: 'guildInvite', ...(who.value.trim() ? { name: who.value.trim() } : {}) }), true)));
    }
  }

  private drawInfo(body: HTMLElement, g: GuildState): void {
    const mine = guildRank(g.rank);
    const lv = guildLevel(g);
    const next = GUILD_LEVELS.find((l) => l.level === lv.level + 1);
    body.append(
      this.row(h('span', { class: 'gd-grow' }, `ระดับ ${lv.level} · ${lv.label}`), h('span', {}, `ตำแหน่ง ${mine.name}`)),
      this.row(h('span', { class: 'gd-grow' }, `แต้มกิลด์ ${fmt(g.contribution)}${next ? ` / ${fmt(next.contribution)}` : ' (สูงสุด)'}`), h('span', {}, `ของท่าน ${fmt(g.own)}`)),
      this.row(h('span', { class: 'gd-grow' }, `คลังกิลด์ ${fmt(g.coffer)} ตำลึง`), h('span', {}, `โบนัสศึก +${Math.round(lv.warBonus * 100)}%`)),
    );
    const notice = h('input', { type: 'text', maxLength: GUILD.noticeMax, value: this.draftNotice ?? g.notice, placeholder: 'ประกาศกิลด์', disabled: !mine.canNotice });
    notice.addEventListener('input', () => (this.draftNotice = notice.value));
    const gold = h('input', { type: 'number', min: String(GUILD.donateMin), step: '100', value: String(GUILD.donateMin) });
    body.append(
      this.row(notice, this.btn('บันทึกประกาศ', () => {
        this.ctx.send({ t: 'guildNotice', text: notice.value });
        this.draftNotice = null;
      }, false, !mine.canNotice)),
      this.row(gold, this.btn(`บริจาค (ขั้นต่ำ ${GUILD.donateMin})`, () => this.ctx.send({ t: 'guildDonate', gold: Number(gold.value) }), true, this.w.progress.gold < GUILD.donateMin)),
      this.row(
        this.btn('ออกจากกิลด์', () => confirm('ออกจากกิลด์ใช่หรือไม่?') && this.ctx.send({ t: 'guildLeave' })),
        ...(g.rank === 'leader' ? [this.btn('ยุบกิลด์', () => confirm(`ยุบกิลด์ ${g.name} ถาวรใช่หรือไม่?`) && this.ctx.send({ t: 'guildDisband' }))] : []),
      ),
    );
  }

  private drawWar(body: HTMLElement, g: GuildState): void {
    const now = localMinute();
    const phase = guildWarPhase(now);
    const untilStart = (((GUILD_WAR.start - now) % 1440) + 1440) % 1440;
    const left = Math.max(0, Math.floor((phase === 'war' ? GUILD_WAR.end - now : untilStart) * 60));
    const clock = `${String(Math.floor(left / 3600)).padStart(2, '0')}:${String(Math.floor((left % 3600) / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
    body.append(
      h('p', { class: 'gd-note' }, `ศึกกิลด์ทุกวัน ${hhmm(GUILD_WAR.start)}–${hhmm(GUILD_WAR.end)} · สมัครได้ถึง ${hhmm(GUILD_WAR.signupClose)}`),
      this.row(h('span', { class: 'gd-grow' }, phase === 'war' ? 'ศึกกำลังดำเนินอยู่ จบในอีก' : 'ศึกเริ่มในอีก'), h('span', { class: 'gd-hot' }, clock)),
    );
    const open = phase === 'signup';
    const modes = this.row();
    for (const m of Object.keys(GUILD_WAR_MODES) as GuildWarMode[]) {
      const b = this.btn(GUILD_WAR_MODES[m].label, () => this.ctx.send({ t: 'guildWarSignup', mode: m }), g.warSignup === m, !open);
      b.setAttribute('aria-pressed', String(g.warSignup === m));
      modes.append(b);
    }
    body.append(
      h('p', { class: 'gd-note' }, g.warSignup ? `สมัครแล้ว: ${GUILD_WAR_MODES[g.warSignup].label}` : open ? 'เลือกคู่ต่อสู้เพื่อสมัครศึกคืนนี้' : 'ปิดรับสมัครแล้ว'),
      modes,
      h('p', { class: 'gd-note' }, this.lastWar ? `ศึกครั้งล่าสุด: ${this.lastWar}` : 'ยังไม่เคยออกศึก'),
    );
  }

  private drawEnemies(body: HTMLElement, g: GuildState): void {
    const B = GUILD_BANNER;
    const officer = B.raiseRanks.includes(g.rank);
    const enemies = g.enemies ?? [];
    const cool = Math.ceil(g.bannerCool ?? 0);
    body.append(
      h('p', { class: 'gd-note' }, `ปักป้ายสังหารใส่กิลด์ศัตรูได้ครั้งละ ${B.duration / 60} นาที (ใช้ ${fmt(B.cost)} ตำลึงจากคลังกิลด์) · ระหว่างนั้นสมาชิกสองกิลด์สู้กันได้โดยไม่ต้องเปิด PK และไม่ได้ค่า PK ทั้งสองฝ่าย · คนนอกยังคิดค่า PK ตามปกติ · เขตปลอดภัยยังสู้ไม่ได้`),
    );
    if (g.banner)
      body.append(
        h(
          'div',
          { class: 'gd-banner' },
          this.row(h('span', { class: 'gd-grow gd-hot' }, `${GUILD_BANNER_MARKS.hunted.glyph} ป้ายสังหาร · ${g.banner.targetName}`), h('span', { class: 'gd-hot' }, fmtClock(Math.ceil(g.banner.left)))),
          h('p', { class: 'gd-note' }, `ผลการสังหาร ฝ่ายเรา ${g.banner.ours} : ${g.banner.theirs} ฝ่ายเขา`),
        ),
      );
    else if (cool > 0) body.append(h('p', { class: 'gd-note' }, `ป้ายสังหารพร้อมอีกครั้งใน ${fmtClock(cool)}`));
    if (!officer) body.append(h('p', { class: 'gd-note' }, 'เจ้ากิลด์และรองเจ้ากิลด์เท่านั้นที่ประกาศกิลด์ศัตรูและปักป้ายสังหารได้'));
    body.append(h('p', { class: 'gd-note gd-hot' }, `กิลด์ศัตรู ${enemies.length} / ${B.enemiesMax}`));
    const canRaise = officer && !g.banner && cool <= 0 && g.coffer >= B.cost;
    for (const id of enemies) {
      const under = g.banner?.target === id;
      body.append(
        this.row(
          h('span', { class: 'gd-grow' }, guildName(this.w, id)),
          this.btn('ปักป้ายสังหาร', () => this.ctx.send({ t: 'guildBannerRaise', id }), true, !canRaise),
          this.btn('ปลด', () => this.ctx.send({ t: 'guildEnemyRemove', id }), false, !officer || under),
        ),
      );
    }
    const others = SIM_GUILDS.filter((s) => s.id !== g.id && !enemies.includes(s.id));
    if (others.length) body.append(h('p', { class: 'gd-note' }, 'กิลด์อื่น'));
    for (const s of others)
      body.append(this.row(h('span', { class: 'gd-grow' }, `${s.name} · ${s.members.length} คน`), this.btn('ประกาศเป็นกิลด์ศัตรู', () => this.ctx.send({ t: 'guildEnemyAdd', id: s.id }), false, !officer || enemies.length >= B.enemiesMax)));
  }
}

registerUi('guild', (ctx) => {
  const ui = new GuildUi(ctx);
  return { handle: (ev) => ui.handle(ev), sync: (dt) => ui.sync(dt), escape: () => ui.escape() };
});
